import { Counter, Tab } from '@cad-arc';
import {
    CadNextNotification,
    CadNextNotificationEntityEnumType,
    CadNextNotificationTypeEnumType,
    EventPanelFormType,
} from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { FieldPath, useFormState } from 'react-hook-form';
import { useEventNotificationsEventId } from '../../../event-notifications/event-notifications-event-id-context';
import { useEventNotificationsStore } from '../../../event-notifications/use-event-notifications';
import { useIntegratedForm } from '../../../forms/use-integrated-form';
import { getAssociatedEventsIds } from '../../../event-notifications/event-notifications-store';
import {
    useActiveEventPanelTab,
    useSetActiveEventPanelTab,
} from '../../active-event-panel-tab-provider';
import { EventNotificationsCounter } from './event-notifications-counter';
import { EventPanelTab } from './event-panel-tabs-config';
import { Tooltip } from '~/common/components/tooltip';
import styles from './event-panel-tabs.module.css';

function badgeStyles(shiftLeft: boolean): React.CSSProperties {
    return {
        // pointerEvents 'none' allows click events on the pointer to be handled
        // by the tab instead
        pointerEvents: 'none',
        position: 'absolute',
        zIndex: 10000 + (shiftLeft ? 1 : 0),
        top: 0,
        right: 3 + (shiftLeft ? 12 : 0),
    };
}

export const TabHeader = observer(function TabHeader(props: {
    notificationTypes?: CadNextNotificationTypeEnumType[];
    notificationsEntityTypes?: CadNextNotificationEntityEnumType[];
    value: EventPanelTab;
    leadingVisual: typeof Tab['leadingVisual'];
    onSelect?: () => void;
    tooltip: string;
    errorFields?: FieldPath<EventPanelFormType>[];
    tabHeaderWidth: string;
}): React.ReactElement {
    const activeTab = useActiveEventPanelTab();
    const setActiveTab = useSetActiveEventPanelTab();
    const eventForm = useIntegratedForm<EventPanelFormType>().form;
    const { errors } = useFormState({
        control: eventForm.control,
        name: props.errorFields ?? [],
        disabled: !props.errorFields || props.errorFields.length === 0,
    });
    const errorCount = (props.errorFields ?? []).reduce((errorCount, errorField) => {
        let localCount = errorCount;
        const errorsForField = errors[errorField as keyof typeof errors];
        if (Array.isArray(errorsForField)) {
            errorsForField.forEach((errorObject) => {
                if (errorObject) {
                    localCount = localCount + Object.keys(errorObject).length;
                }
            });
        }
        return localCount;
    }, 0);
    const eventNotificationsStore = useEventNotificationsStore();
    const eventNotificationsEventId = useEventNotificationsEventId();

    const notifications =
        props.notificationTypes && eventNotificationsEventId
            ? (props.notificationTypes
                  .map((notificationType) => {
                      if (notificationType === 'ASSOCIATED_EVENT') {
                          const associatedEventsIds = getAssociatedEventsIds(
                              eventNotificationsStore.getState(),
                              eventNotificationsEventId
                          );

                          return associatedEventsIds?.map((entityId) =>
                              eventNotificationsStore.getNotification({
                                  eventId: eventNotificationsEventId,
                                  entityId: Number(entityId),
                                  notification: notificationType,
                              })
                          );
                      } else {
                          return eventNotificationsStore.getNotification({
                              eventId: eventNotificationsEventId,
                              entityId: eventNotificationsEventId,
                              notification: notificationType,
                          });
                      }
                  })
                  .flat()
                  .filter((notification) => !!notification) as CadNextNotification[])
            : [];

    const notificationsForEntityTypes =
        props.notificationsEntityTypes && eventNotificationsEventId
            ? props.notificationsEntityTypes
                  .map((notificationEntityType) => {
                      return (
                          eventNotificationsStore.getAllNotificationsForEntityTypeForEvent(
                              eventNotificationsEventId,
                              notificationEntityType
                          ) ?? []
                      );
                  })
                  .flat()
            : [];

    // NOTE: This will also handle keyboard accessibilty, thanks to radix
    const handleClick = useCallback(() => {
        setActiveTab(activeTab === props.value ? undefined : props.value);
    }, [activeTab, props.value, setActiveTab]);

    return (
        <Tooltip overlay={props.tooltip}>
            <div style={{ position: 'relative', minWidth: props.tabHeaderWidth }}>
                {errorCount > 0 ? (
                    <Counter
                        style={badgeStyles(
                            notifications.length > 0 || notificationsForEntityTypes.length > 0
                                ? true
                                : false
                        )}
                        variant="important"
                    >
                        {errorCount}
                    </Counter>
                ) : undefined}
                <EventNotificationsCounter
                    notifications={
                        notifications.length > 0 ? notifications : notificationsForEntityTypes
                    }
                    style={badgeStyles(false)}
                />
                <Tab
                    data-testid={props.value}
                    value={props.value}
                    className={styles.tab}
                    onClick={handleClick}
                    leadingVisual={props.leadingVisual}
                />
            </div>
        </Tooltip>
    );
});
